import { alpha, Loader, rem } from "@mantine/core";
import { useDisclosure, useHover, useMergedRef } from "@mantine/hooks";
import { Stack } from "../../../stack";
import { Box } from "../../../box";
import { faBolt, faEllipsis, faTag, IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { useTheme } from "../../../../hooks";
import { Text } from "../../../text";
import {
    forwardRef,
    isValidElement,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Group } from "../../../group";
import { Icon } from "../../../icon";
import styles from "./chat.module.scss";
import {
    IInteractionMessage,
    InteractionMessageType,
    InteractionType,
    IPhraseTriggerResult,
    ITag,
    ITagGroup,
    MessageDirection,
    Sentiment,
    sentimentLabels,
    TagGroupType,
} from "@edgetier/watchtower-types";
import { SentimentIcon } from "../../../sentiment-icon";
import { Pill } from "../../../pill";
import { useInteractionModalContext } from "../context/context";
import { groupBy } from "@edgetier/utilities";
import { format, formatDate } from "date-fns";
import { faTagExternal } from "@awesome.me/kit-67bf90e9a4/icons/kit/custom";
import { Skeleton } from "../../../skeleton";
import { Menu } from "../../../menu";
import { ActionIcon } from "../../../action-icon";
import { Checkbox } from "../../../checkbox";
import { Tooltip } from "../../../tooltip";
import { Markdown } from "../../../markdown";
import { formatClassNamesMessage, formatWritingIssueMessage } from "./chat.utilities";
import { MESSAGE_TIME_FORMAT } from "./chat.constants";
import { kebabCase } from "lodash";
import { Popover } from "../../../popover";
import { useAudioContext } from "../../audio-context";

const DayBadge = ({ text }: { text: string }) => {
    const theme = useTheme();
    return (
        <Box pos="sticky" top={0} bdrs="xl" bg={alpha(theme.black, 0.6)} p="sm" style={{ zIndex: 1 }}>
            <Text size="xs" c="white" data-testid="day-badge">
                {text}
            </Text>
        </Box>
    );
};
const DayBadgeSkeleton = () => {
    return <Skeleton height={30} width={180} />;
};
DayBadge.Skeleton = DayBadgeSkeleton;

const StatusUpdate = ({
    icon,
    updateText,
    user,
    sendDateTime,
}: {
    icon?: IconDefinition;
    updateText: string;
    user?: string;
    sendDateTime: string;
}) => {
    return (
        <Group py="lg" gap="sm">
            {icon && <Icon size="sm" color="gray" icon={icon} />}
            <Stack gap="xxs" align="center">
                <Text dimmed size="sm" data-testid="status-update-by-user">
                    <Markdown
                        markdownString={updateText}
                        textProps={{ dimmed: true, span: true }}
                        imageProps={{ maw: "90%", preview: true }}
                        anchorProps={{ style: { wordBreak: "break-word" } }}
                    />
                    {user}
                </Text>
                <Text dimmed size="xxs" data-testid="status-update-send-date-time">
                    {sendDateTime}
                </Text>
            </Stack>
        </Group>
    );
};
const StatusUpdateSkeleton = () => {
    return <Text.Skeleton size="sm" my="lg" width="70%" />;
};
StatusUpdate.Skeleton = StatusUpdateSkeleton;

const MessageAuthor = ({
    author,
    messageIcons,
}: {
    messageIcons?: Array<Sentiment | InteractionMessageType.System>;
    author: string;
}) => {
    const iconOptions = useMemo(() => {
        return (
            messageIcons?.map((icon: Sentiment | InteractionMessageType.System) => {
                switch (icon) {
                    case Sentiment.Frustration:
                        return {
                            component: <SentimentIcon.Frustration key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-error-default)",
                        };
                    case Sentiment.Confusion:
                        return {
                            component: <SentimentIcon.Confusion key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-warning-default)",
                        };
                    case Sentiment.Gratitude:
                        return {
                            component: <SentimentIcon.Gratitude key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-primary-default)",
                        };
                    case Sentiment.Praise:
                        return {
                            component: <SentimentIcon.Praise key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-success-default)",
                        };
                    case Sentiment.Empathy:
                        return {
                            component: <SentimentIcon.Empathy key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-violet-default)",
                        };
                    case InteractionMessageType.System:
                        return {
                            component: (
                                <Icon
                                    icon={faBolt}
                                    key={`sentiment-${icon}`}
                                    size="xs"
                                    color="var(--mantine-color-neutral-6)"
                                />
                            ),
                            color: "dimmed",
                        };
                }
            }) ?? []
        );
    }, [messageIcons]);

    const color = useMemo(() => {
        if (typeof iconOptions === "undefined" || iconOptions.length === 0) {
            return "dimmed";
        }
        return iconOptions[iconOptions.length - 1].color;
    }, [iconOptions]);

    return (
        <Group gap="xs" c={color}>
            {iconOptions.map((item) => item.component)}
            <Text variant="medium" size="xs" data-testid="chat-author-name">
                {author}
            </Text>
        </Group>
    );
};
const MessageAuthorSkeleton = () => {
    return <Text.Skeleton size="xs" width={150} />;
};
MessageAuthor.Skeleton = MessageAuthorSkeleton;

const Tag = forwardRef<
    HTMLDivElement,
    {
        icon: IconDefinition;
        text: string;
        editTagButton?: ReactNode;
        removeTagButton?: ReactNode;
        tagTooltipContent?: ReactNode;
    }
>(({ icon, text, editTagButton, removeTagButton, tagTooltipContent }, ref) => {
    const { elements } = useInteractionModalContext();
    const { hovered, ref: hoverRef } = useHover();
    const [opened, { close, open }] = useDisclosure(false);
    const [menuOpened, { close: closeMenu, toggle: toggleMenu }] = useDisclosure(false, {});
    const mergedRef = useMergedRef(ref, hoverRef);

    return (
        <Popover
            position="bottom"
            disabled={typeof elements?.tags?.tooltipContent === "undefined"}
            withArrow
            shadow="md"
            opened={opened && !menuOpened}
        >
            <Popover.Target>
                <Pill
                    variant="subtle"
                    rightSection={
                        isValidElement(editTagButton) || isValidElement(removeTagButton) ? (
                            <Menu
                                keepMounted
                                opened={menuOpened}
                                closeOnClickOutside
                                closeOnItemClick
                                onClose={() => {
                                    closeMenu();
                                    close();
                                }}
                                onOpen={close}
                            >
                                <Menu.Target>
                                    <ActionIcon
                                        onClick={toggleMenu}
                                        size="xs"
                                        variant="subtle"
                                        c="var(--mantine-color-text)"
                                        icon={hovered ? faEllipsis : icon}
                                    />
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {editTagButton}
                                    {removeTagButton}
                                </Menu.Dropdown>
                            </Menu>
                        ) : (
                            <Icon icon={icon} size="xs" />
                        )
                    }
                    ref={mergedRef}
                    data-testid={`chat-message-tag-${kebabCase(text)}`}
                    onMouseEnter={open}
                    onMouseLeave={close}
                >
                    {text}
                </Pill>
            </Popover.Target>
            {elements?.tags?.tooltipContent && (
                <Popover.Dropdown miw={rem(380)} maw={rem(500)}>
                    {tagTooltipContent}
                </Popover.Dropdown>
            )}
        </Popover>
    );
});
Tag.displayName = "Tag";

const MessageOptionsSentiment = ({
    value,
    sentiment,
    onSentimentToggle,
}: {
    value: boolean;
    sentiment: Sentiment;
    onSentimentToggle: (sentiment: Sentiment) => Promise<void>;
}) => {
    const [isInternalLoading, setIsInternalLoading] = useState(false);

    const handleConfirm = useCallback(
        (sentiment: Sentiment) => {
            try {
                const result = onSentimentToggle(sentiment);

                // If result is a thenable (Promise-like), show loading state until it settles
                if (result && typeof result.then === "function") {
                    setIsInternalLoading(true);
                    return Promise.resolve(result)
                        .then((res) => {
                            setIsInternalLoading(false);
                            return res;
                        })
                        .catch((err) => {
                            setIsInternalLoading(false);
                            throw err;
                        });
                }

                // Synchronous result: return it directly, no loading state
                return result;
            } catch (err) {
                // If onConfirm throws synchronously, just rethrow
                throw err;
            }
        },
        [onSentimentToggle]
    );

    return (
        <Menu.Item
            key={`sentiment-toggler-${sentimentLabels[sentiment as Sentiment]}`}
            component="div"
            closeMenuOnClick={false}
            disabled={isInternalLoading}
            onClick={(event) => {
                event.preventDefault();
                handleConfirm(sentiment as Sentiment);
            }}
        >
            <Checkbox
                styles={{ labelWrapper: { flex: 1 } }}
                classNames={{ label: styles["checkbox-label"], input: styles["checkbox-input"] }}
                labelPosition="left"
                checked={value as boolean}
                label={
                    <Group>
                        {isInternalLoading ? (
                            <Loader size="xs" />
                        ) : (
                            <SentimentIcon sentiment={sentiment as Sentiment} />
                        )}
                        {sentimentLabels[sentiment as Sentiment]}
                    </Group>
                }
                data-testid={`${kebabCase(sentimentLabels[sentiment as Sentiment])}`}
                readOnly
            />
        </Menu.Item>
    );
};

const MessageOptions = ({
    visible,
    hasFrustration,
    hasConfusion,
    hasGratitude,
    hasPraise,
    hasEmpathy,
    onSentimentToggle,
    sendDateTime,
    messageDirection,
    interactionType,
    addTagButton,
    messageAudioSeek,
}: {
    visible: boolean;
    hasFrustration?: boolean;
    hasConfusion?: boolean;
    hasGratitude?: boolean;
    hasPraise?: boolean;
    hasEmpathy?: boolean;
    onSentimentToggle: ((sentiment: Sentiment) => Promise<void>) | undefined;
    sendDateTime: Date;
    messageDirection: MessageDirection;
    interactionType: InteractionType;
    addTagButton?: React.ReactNode;
    messageAudioSeek?: (props: {
        sendDateTime: Date;
        messageDirection: MessageDirection;
        isVisible: boolean;
    }) => React.ReactNode;
}) => {
    const audioContext = useAudioContext();
    const [opened, setOpened] = useState(false);

    const hasAddTagButton = isValidElement(addTagButton);
    const hasMessageSentimentOptions = typeof onSentimentToggle !== "undefined";
    const hasBothMenuOptions = hasAddTagButton && hasMessageSentimentOptions;

    // We should only show the audio seek icon when the messageAudioSeek prop is provided and when the interaction is a
    // call. The position of the icon also changes depending on if the message is inbound or outbound.
    const isMessageAudioSeekAvailable = useMemo(() => {
        const isAvailable = messageAudioSeek && interactionType === InteractionType.Call && !audioContext?.error;
        const isBeforeMenu = messageDirection === MessageDirection.Outbound;
        return { isAvailable, isBeforeMenu };
    }, [audioContext?.error, interactionType, messageAudioSeek, messageDirection]);

    // If the interaction is a call, we show an extra icon next to the menu. In this case, we need to change the border
    // radius of the menu icon to fit correctly with the extra icon.
    const menuIconBorderRadius = useCallback(
        (radius: string) => {
            if (isMessageAudioSeekAvailable.isAvailable) {
                return messageDirection === MessageDirection.Outbound
                    ? `0 ${radius} ${radius} 0`
                    : `${radius} 0 0 ${radius}`;
            }
            return undefined;
        },
        [isMessageAudioSeekAvailable.isAvailable, messageDirection]
    );

    if (!hasAddTagButton && !hasMessageSentimentOptions) {
        return null;
    }

    return (
        <Box>
            <Group gap="0" wrap="nowrap">
                {isMessageAudioSeekAvailable.isAvailable &&
                    isMessageAudioSeekAvailable.isBeforeMenu &&
                    messageAudioSeek!({ sendDateTime, messageDirection, isVisible: visible })}
                <Menu
                    keepMounted
                    withinPortal={false}
                    opened={opened}
                    onOpen={() => setOpened(true)}
                    onClose={() => setOpened(false)}
                >
                    <Menu.Target data-testid="chat-actions-menu-target">
                        <ActionIcon
                            size="sm"
                            color="gray"
                            variant="default"
                            styles={(theme) => ({
                                root: {
                                    visibility: visible ? "visible" : "hidden",
                                    borderRadius: menuIconBorderRadius(theme.radius.md),
                                },
                            })}
                        >
                            <Icon icon={faEllipsis} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown w={174} onMouseLeave={() => setOpened(false)}>
                        {hasAddTagButton && addTagButton}
                        {hasBothMenuOptions && <Menu.Divider />}
                        {hasMessageSentimentOptions &&
                            (
                                [
                                    [Sentiment.Frustration, hasFrustration],
                                    [Sentiment.Confusion, hasConfusion],
                                    [Sentiment.Gratitude, hasGratitude],
                                    [Sentiment.Praise, hasPraise],
                                    [Sentiment.Empathy, hasEmpathy],
                                ] as [Sentiment, boolean][]
                            )
                                .filter(([, value]) => typeof value !== "undefined")
                                .map(([sentiment, value]) => (
                                    <MessageOptionsSentiment
                                        key={`sentiment-toggler-${sentimentLabels[sentiment as Sentiment]}`}
                                        sentiment={sentiment}
                                        value={value}
                                        onSentimentToggle={onSentimentToggle}
                                    />
                                ))}
                    </Menu.Dropdown>
                </Menu>
                {isMessageAudioSeekAvailable.isAvailable &&
                    !isMessageAudioSeekAvailable.isBeforeMenu &&
                    messageAudioSeek!({ sendDateTime, messageDirection, isVisible: visible })}
            </Group>
        </Box>
    );
};

const MessagesList = ({ direction, children }: PropsWithChildren<{ direction: MessageDirection }>) => {
    return (
        <Stack align={direction === MessageDirection.Outbound ? "flex-end" : "flex-start"} w="100%">
            {children}
        </Stack>
    );
};

const Message = ({
    messageId,
    direction,
    message,
    time,
    author,
    tags,
    sentiments,
    onSentimentToggle,
    addTagButton,
    sendDateTime,
    interactionType,
}: {
    direction: MessageDirection;
    messageId: string;
    message: string;
    sentiments: {
        hasFrustration?: boolean;
        hasConfusion?: boolean;
        hasGratitude?: boolean;
        hasPraise?: boolean;
        hasEmpathy?: boolean;
    };
    time: string;
    author?: React.ReactNode;
    tags?: (ITag & { isExternal: boolean })[];
    onSentimentToggle: ((sentiment: Sentiment) => Promise<void>) | undefined;
    addTagButton: React.ReactNode;
    sendDateTime: Date;
    interactionType: InteractionType;
}) => {
    const {
        interactionWritingIssues,
        actions,
        elements,
        interactionMessages: { highlightedMessageId, setHighlightedMessageId },
    } = useInteractionModalContext();
    const theme = useTheme();
    const { hovered, ref } = useHover();
    const messageRef = useRef<HTMLDivElement>(null);
    const mergedMessageRef = useMergedRef(ref, messageRef);
    const messageContentRef = useRef<HTMLDivElement>(null);

    const messageOptions = useMemo(
        () => (
            <MessageOptions
                {...sentiments}
                messageAudioSeek={elements?.audio?.messageAudioSeek}
                sendDateTime={sendDateTime}
                messageDirection={direction}
                interactionType={interactionType}
                addTagButton={addTagButton}
                onSentimentToggle={onSentimentToggle}
                visible={hovered}
            />
        ),
        [
            sentiments,
            elements?.audio?.messageAudioSeek,
            sendDateTime,
            direction,
            interactionType,
            addTagButton,
            onSentimentToggle,
            hovered,
        ]
    );

    // Scroll modal to message when message is highlighted
    useEffect(() => {
        if (messageRef.current && highlightedMessageId === messageId) {
            requestAnimationFrame(() => {
                messageRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                // Slight delay so the focus happens after the scroll
                const focusTimer = setTimeout(() => {
                    messageContentRef.current?.setAttribute("data-focused", "true");
                }, 100);

                const unfocusTimer = setTimeout(() => {
                    messageContentRef.current?.removeAttribute("data-focused");
                }, 2000);

                return () => {
                    clearTimeout(focusTimer);
                    clearTimeout(unfocusTimer);
                };
            });
        }
    }, [highlightedMessageId, messageId]);

    // Remove highlight from message on click
    const unHighlightMessage = useCallback(() => {
        setHighlightedMessageId(null);
        messageContentRef.current?.removeAttribute("data-focused");
    }, [setHighlightedMessageId]);

    const isMessageLoading = useMemo(() => {
        const translation = actions?.translation?.activeTranslations[messageId] ?? null;
        const isTranslationLoading = translation?.isLoading;
        const isWritingIssuesLoading = actions?.writingIssues?.isEnabled && interactionWritingIssues.isLoading;
        return isTranslationLoading || isWritingIssuesLoading;
    }, [
        messageId,
        actions?.translation?.activeTranslations,
        actions?.writingIssues?.isEnabled,
        interactionWritingIssues.isLoading,
    ]);

    const [preparedMessage, setPreparedMessage] = useState<string>(message);

    useEffect(() => {
        setPreparedMessage(message);
    }, [message]);

    const toggleSentenceHighlight = useCallback(
        (isOpen: boolean, phraseTriggerTagDetails: IPhraseTriggerResult) => {
            const { sentence } = phraseTriggerTagDetails;
            if (isOpen) {
                // Highlight the phrase trigger sentence
                setPreparedMessage(
                    formatClassNamesMessage([sentence], message, {
                        clearPrevious: true,
                        className: styles["highlight"],
                    })
                );
            } else {
                // Remove the highlight from phrase trigger sentence
                setPreparedMessage(formatClassNamesMessage([], message, { clearPrevious: true }));
            }
        },
        [message]
    );

    const messageContent = useMemo(() => {
        let messageContent: string;
        const isTranslationEnabled = actions?.translation?.isEnabled ?? false;
        const translation = actions?.translation?.activeTranslations[messageId] ?? null;
        if (isTranslationEnabled && !translation?.isError) {
            if (!!translation?.translatedText) {
                messageContent = translation.translatedText;
            } else {
                messageContent = preparedMessage;
            }
        } else {
            messageContent = preparedMessage;
        }

        if (actions?.writingIssues?.isEnabled) {
            const messageWritingIssues = interactionWritingIssues.data
                ? (interactionWritingIssues.data?.[messageId] ?? [])
                : [];
            return formatWritingIssueMessage(messageWritingIssues, messageContent);
        }
        return messageContent;
    }, [
        actions?.translation?.isEnabled,
        actions?.writingIssues?.isEnabled,
        actions?.translation?.activeTranslations,
        interactionWritingIssues.data,
        preparedMessage,
        messageId,
    ]);

    return (
        <Stack
            className={styles["message"]}
            data-direction={direction.toLocaleLowerCase()}
            maw="75%"
            align={direction === MessageDirection.Outbound ? "flex-end" : "flex-start"}
            gap="xs"
            ref={mergedMessageRef}
            onClick={unHighlightMessage}
        >
            <Group gap="sm">{author}</Group>
            <Group wrap="nowrap" align="flex-start">
                {direction === MessageDirection.Outbound && messageOptions}
                <Box
                    p="md"
                    bg={
                        direction === MessageDirection.Outbound
                            ? "var(--surface-primary-solid-color)"
                            : "var(--surface-gray-fade)"
                    }
                    c={
                        direction === MessageDirection.Outbound
                            ? "var(--mantine-color-text-permanent-1)"
                            : "var(--mantine-color-neutral-9)"
                    }
                    maw={rem(540)}
                    style={
                        direction === MessageDirection.Outbound
                            ? {
                                  borderBottomRightRadius: theme.radius.lg,
                                  borderBottomLeftRadius: theme.radius.lg,
                                  borderTopRightRadius: 0,
                                  borderTopLeftRadius: theme.radius.lg,
                              }
                            : {
                                  borderBottomRightRadius: theme.radius.lg,
                                  borderBottomLeftRadius: theme.radius.lg,
                                  borderTopRightRadius: theme.radius.lg,
                                  borderTopLeftRadius: 0,
                              }
                    }
                    className={styles["message-content"]}
                    ref={messageContentRef}
                    tabIndex={-1}
                >
                    <Stack>
                        <Skeleton
                            color={
                                direction === MessageDirection.Outbound
                                    ? "var(--surface-primary-solid-color)"
                                    : undefined
                            }
                            visible={isMessageLoading ?? false}
                        >
                            <Markdown
                                markdownString={messageContent}
                                imageProps={{ maw: 100, preview: true }}
                                listProps={{ size: "sm" }}
                                textProps={{ "data-testid": "chat-message-content", size: "sm" }}
                            />
                        </Skeleton>
                        {tags && tags.length > 0 && (
                            <Group gap="xs">
                                {tags?.map((messageTag, tagIndex) => {
                                    const { tag, tagId, tagGroup, isExternal } = messageTag;
                                    return (
                                        <Tag
                                            key={`tag-${tagIndex}`}
                                            icon={isExternal ? (faTagExternal as IconDefinition) : faTag}
                                            text={tag}
                                            editTagButton={
                                                elements?.tags?.editTagButton && (
                                                    <elements.tags.editTagButton tagId={tagId} messageId={messageId} />
                                                )
                                            }
                                            removeTagButton={
                                                elements?.tags?.removeTagButton ? (
                                                    <elements.tags.removeTagButton
                                                        tag={messageTag}
                                                        messageId={messageId}
                                                    />
                                                ) : null
                                            }
                                            tagTooltipContent={
                                                elements?.tags?.tooltipContent && (
                                                    <elements.tags.tooltipContent
                                                        tag={tag}
                                                        tagId={tagId}
                                                        tagGroup={tagGroup}
                                                        messageId={messageId}
                                                        onMouseEnter={(details) => {
                                                            if (details) {
                                                                toggleSentenceHighlight(true, details);
                                                            }
                                                        }}
                                                        onMouseLeave={(details) => {
                                                            if (details) {
                                                                toggleSentenceHighlight(false, details);
                                                            }
                                                        }}
                                                    />
                                                )
                                            }
                                        />
                                    );
                                })}
                            </Group>
                        )}
                    </Stack>
                </Box>
                {direction === MessageDirection.Inbound && messageOptions}
            </Group>
            <Text variant="medium" size="xxs" dimmed>
                {time}
            </Text>
        </Stack>
    );
};

const MessageSkeleton = ({ direction }: { direction: MessageDirection }) => {
    const theme = useTheme();
    return (
        <Stack w="70%" align={direction === MessageDirection.Outbound ? "flex-end" : "flex-start"} gap="xs">
            <Text.Skeleton size="xs" width={150} />
            <Skeleton
                height={80}
                style={
                    direction === MessageDirection.Outbound
                        ? {
                              borderBottomRightRadius: theme.radius.lg,
                              borderBottomLeftRadius: theme.radius.lg,
                              borderTopRightRadius: 0,
                              borderTopLeftRadius: theme.radius.lg,
                          }
                        : {
                              borderBottomRightRadius: theme.radius.lg,
                              borderBottomLeftRadius: theme.radius.lg,
                              borderTopRightRadius: theme.radius.lg,
                              borderTopLeftRadius: 0,
                          }
                }
            />
            <Text.Skeleton size="xxs" width={100} />
        </Stack>
    );
};

Message.Skeleton = MessageSkeleton;

const LoadingChat = () => {
    return (
        <Stack align="center" w="100%" justify="flex-start">
            <DayBadge.Skeleton />
            <StatusUpdate.Skeleton />
            <MessagesList direction={MessageDirection.Outbound}>
                <Message.Skeleton direction={MessageDirection.Outbound} />
                <Message.Skeleton direction={MessageDirection.Outbound} />
            </MessagesList>
            <MessagesList direction={MessageDirection.Inbound}>
                <Message.Skeleton direction={MessageDirection.Inbound} />
            </MessagesList>
            <MessagesList direction={MessageDirection.Outbound}>
                <Message.Skeleton direction={MessageDirection.Outbound} />
            </MessagesList>
            <MessagesList direction={MessageDirection.Inbound}>
                <Message.Skeleton direction={MessageDirection.Inbound} />
            </MessagesList>
        </Stack>
    );
};

export const Chat = () => {
    const {
        elements,
        interactionMessages,
        interactionMessagesAgents,
        interactionMessagesTagGroups,
        actions,
        interactionDetails,
    } = useInteractionModalContext();
    const messagesGroupedByDate = useMemo(
        () => groupBy(interactionMessages.data ?? [], ({ sendDateTime }) => format(sendDateTime, "cccc, do LLLL yyyy")),
        [interactionMessages]
    );
    const messagesAgentsNames = useMemo(
        () =>
            interactionMessagesAgents.data?.reduce<Record<string, string>>((agentNames, agent) => {
                return {
                    ...agentNames,
                    [agent.agentId]: agent.name,
                };
            }, {}) ?? {},
        [interactionMessagesAgents.data]
    );

    const tagGroupsById = useMemo(
        () =>
            interactionMessagesTagGroups.data?.reduce<Record<string, ITagGroup>>((tagGroups, tagGroup) => {
                return {
                    ...tagGroups,
                    [tagGroup.tagGroupId]: tagGroup,
                };
            }, {}) ?? {},
        [interactionMessagesTagGroups.data]
    );

    const senderName = useMemo(() => {
        return interactionDetails.data?.senderName ?? "";
    }, [interactionDetails.data]);

    const interactionType = useMemo(() => {
        return interactionDetails.data?.interactionType ?? InteractionType.Chat;
    }, [interactionDetails.data?.interactionType]);

    const getMessageAuthor = useCallback(
        (message: IInteractionMessage) => {
            if (message.direction === MessageDirection.Inbound) {
                return senderName;
            }
            if (message.isBot) {
                if (message.agentId !== null) {
                    return messagesAgentsNames[message.agentId] ?? "Automated";
                }
                return "Automated";
            }
            if (message.agentId !== null) {
                return messagesAgentsNames[message.agentId] ?? "";
            }
            return "";
        },
        [senderName, messagesAgentsNames]
    );

    return (
        <Stack justify="flex-start" w="100%" className="interaction-messages">
            {interactionMessages.isLoading && <LoadingChat />}
            {!interactionMessages.isLoading &&
                Object.entries(messagesGroupedByDate)
                    .filter(([date, messages]) =>
                        messages.every((message) => message.messageType !== InteractionMessageType.SuggestedResponse)
                    )
                    .map(([date, messages], index) => (
                        <Stack align="center" justify="flex-start" maw="100%" key={date}>
                            <DayBadge text={date} key={date} />
                            {messages.map((message, messageIndex) => {
                                if (message.messageType === InteractionMessageType.SuggestedResponse) {
                                    return null;
                                }
                                const messageIcons: Array<Sentiment | InteractionMessageType.System> = [];
                                if (message.direction === MessageDirection.Inbound) {
                                    if (message.hasFrustration) messageIcons.push(Sentiment.Frustration);
                                    if (message.hasConfusion) messageIcons.push(Sentiment.Confusion);
                                    if (message.hasGratitude) messageIcons.push(Sentiment.Gratitude);
                                    if (message.hasPraise) messageIcons.push(Sentiment.Praise);
                                } else {
                                    if (message.isBot) messageIcons.push(InteractionMessageType.System);
                                    else {
                                        if (message.hasEmpathy) messageIcons.push(Sentiment.Empathy);
                                    }
                                }

                                if (!message.interactionMessageId) return null;
                                if (message.messageType === InteractionMessageType.System) {
                                    return (
                                        <StatusUpdate
                                            key={`${date}-message-${messageIndex}`}
                                            updateText={message.message}
                                            sendDateTime={formatDate(message.sendDateTime, MESSAGE_TIME_FORMAT)}
                                        />
                                    );
                                }
                                const author = getMessageAuthor(message);
                                return (
                                    <MessagesList key={`chat-message-${messageIndex}`} direction={message.direction}>
                                        <Message
                                            messageId={message.interactionMessageId}
                                            direction={message.direction}
                                            sendDateTime={message.sendDateTime}
                                            interactionType={interactionType}
                                            author={
                                                interactionMessagesAgents.isLoading ? (
                                                    <MessageAuthor.Skeleton />
                                                ) : (
                                                    <Tooltip
                                                        label={
                                                            elements?.sentiments?.tooltipContent ? (
                                                                <elements.sentiments.tooltipContent
                                                                    sentiments={
                                                                        message.direction === MessageDirection.Outbound
                                                                            ? { hasEmpathy: message.hasEmpathy }
                                                                            : {
                                                                                  hasFrustration:
                                                                                      message.hasFrustration,
                                                                                  hasConfusion: message.hasConfusion,
                                                                                  hasGratitude: message.hasGratitude,
                                                                                  hasPraise: message.hasPraise,
                                                                              }
                                                                    }
                                                                    messageId={message.interactionMessageId}
                                                                />
                                                            ) : (
                                                                ""
                                                            )
                                                        }
                                                        disabled={
                                                            typeof elements?.sentiments?.tooltipContent ===
                                                                "undefined" ||
                                                            messageIcons.length === 0 ||
                                                            message.isBot
                                                        }
                                                    >
                                                        <Box>
                                                            <MessageAuthor
                                                                author={author}
                                                                messageIcons={messageIcons}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                )
                                            }
                                            onSentimentToggle={
                                                typeof actions?.sentiments?.onToggle === "function"
                                                    ? (sentiment) =>
                                                          actions!.sentiments!.onToggle(
                                                              message.interactionMessageId,
                                                              sentiment
                                                          )
                                                    : undefined
                                            }
                                            sentiments={
                                                message.direction === MessageDirection.Outbound
                                                    ? { hasEmpathy: message.hasEmpathy }
                                                    : {
                                                          hasFrustration: message.hasFrustration,
                                                          hasConfusion: message.hasConfusion,
                                                          hasGratitude: message.hasGratitude,
                                                          hasPraise: message.hasPraise,
                                                      }
                                            }
                                            message={message.message}
                                            time={formatDate(message.sendDateTime, MESSAGE_TIME_FORMAT)}
                                            addTagButton={
                                                elements?.tags?.addTagButton ? (
                                                    <elements.tags.addTagButton
                                                        messageId={message.interactionMessageId}
                                                    />
                                                ) : null
                                            }
                                            tags={message.tags?.map((tag) => ({
                                                ...tag,
                                                isExternal:
                                                    tagGroupsById[tag.tagGroupId]?.tagGroupType ===
                                                    TagGroupType.External,
                                            }))}
                                        />
                                    </MessagesList>
                                );
                            })}
                        </Stack>
                    ))}
        </Stack>
    );
};
